/* eslint-disable prettier/prettier */
import { Grid } from '@mui/material';
import { RoutalPalette, TimeIcon, VSpacer } from '@nx-smartmonkey/ui';
import { ReactElement, forwardRef, useState } from 'react';
import styled from 'styled-components';

import tinycolor from 'tinycolor2';
import EndIcon from '../../../../assets/svg/EndRouteIcon';
import StartIcon from '../../../../assets/svg/HomeIcon';
import InstructionIcon from '../../../../assets/svg/InstructionIcon';
import WarningIcon from '../../../../assets/svg/WarningIcon';
import { ROUTE_START_LOCATION, ROUTE_STOPS_BLACKLIST } from '../../../../constants/Routes';
import { Stop, StopType } from '../../../../domain/stops/Stop';
import { Text } from '../../../../react-components/Text';

// Fer-lo much more versatil i generic
export type BarStyle = `void` | `highlighted` | `unhighlighted`;
export type TagStyle = `highlighted` | `unhighlighted` | `start` | `end`;

export const TagContainer = styled.div<{ tagStyle: TagStyle; width?: string; height?: string }>`
  padding: ${(props) => (props.tagStyle === `highlighted` ? `0px` : `0px 0px`)};
  width: ${(props) => props.width || `24px`};
  height: ${(props) => props.height || `24px`};
`;

export const TagText = ({
  tagStyle,
  mainColor,
  secondaryColor,
  transparent,
  stopType,
  children,
  height,
  width,
}: React.PropsWithChildren<{
  tagStyle: string;
  mainColor: string;
  secondaryColor: string;
  transparent: string;
  stopType?: StopType;
  height?: string;
  width?: string;
}>) => {
  return (
    <Text
      style={{
        background:
          tagStyle === `highlighted` ? mainColor : tagStyle === `unhighlighted` ? secondaryColor : transparent,
        color:
          tagStyle === `highlighted` ? RoutalPalette.white : tagStyle === `unhighlighted` ? mainColor : transparent,
        border: `2px solid ${mainColor}`,
        borderRadius: stopType === `pickup` ? `10px` : `50%`,
        display: `flex`,
        justifyContent: `center`,
        alignItems: `center`,
        width: width || `20px`,
        height: height || `20px`,
        maxHeight: `20px`,
        fontSize: `12px`,
        fontWeight: `bold`,
      }}
    >
      {children}
    </Text>
  );
};

export const LabelStop = ({
  labelStyles = {},
  children,
}: React.PropsWithChildren<{ labelStyles?: React.CSSProperties }>) => {
  if (children) {
    return (
      <Text
        style={{
          ...labelStyles,
          overflow: `hidden`,
          textOverflow: `ellipsis`,
          margin: `3px 0px`,
          lineHeight: `1.5em`,
          maxHeight: `3em`,
          display: `-webkit-box`,
          WebkitLineClamp: 2,
          WebkitBoxOrient: `vertical`,
        }}
      >
        {children}
      </Text>
    );
  }

  return (
    <Text
      intlId="stop.label_stop.empty"
      style={{
        ...labelStyles,
        overflow: `hidden`,
        textOverflow: `ellipsis`,
        margin: `8px 0px 3px 0px`,
        lineHeight: `1.5em`,
        maxHeight: `3em`,
        display: `-webkit-box`,
        WebkitLineClamp: 2,
        WebkitBoxOrient: `vertical`,
        color: RoutalPalette.grey7,
        fontStyle: `italic`,
      }}
    />
  );
};

const BarDiv = styled.div<{ barStyle: string; routeColor: string; unselectedColor: string; transparent: string }>`
  width: 2.5px;
  height: 100%;
  alignself: center;
  background: ${(props) =>
    props.barStyle === `highlighted`
      ? props.routeColor
      : props.barStyle === `unhighlighted`
      ? props.unselectedColor
      : props.transparent};
`;

type StopPillProps = {
  stop: Stop;
  stopType?: StopType;
  showCustomerField?: boolean;
  showInProgress?: boolean;
  showOptimizationWarning?: boolean;
  done?: boolean;

  componentColor: string;
  secondaryColor?: string;

  topStyle: BarStyle;
  botStyle: BarStyle;
  tagStyle: TagStyle;

  containerStyles?: React.CSSProperties;
  additionalChips?: React.ReactElement[];

  onSelect?: (stop_id: string) => void;
};

export const StopPill = forwardRef<HTMLDivElement, StopPillProps>(
  (
    {
      stop,
      stopType,
      showCustomerField = false,
      showInProgress = false,
      showOptimizationWarning = false,
      done = false,
      componentColor,
      secondaryColor,
      topStyle,
      botStyle,
      tagStyle,
      additionalChips,
      containerStyles,
      onSelect,
    },
    ref
  ) => {
    const colors = {
      mainColor: componentColor,
      routeColor: RoutalPalette.routeBaseColor,
      secondaryColor: secondaryColor ?? tinycolor(componentColor).lighten(35).toString(),
      unselectedColor: RoutalPalette.grey4,
      transparent: `transparent`,
      pressed: RoutalPalette.neutral05,
    };

    const [isPressed, setIsPressed] = useState(false);
    const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

    const handleMouseDown = () => {
      setIsPressed(true);
    };

    const handleMouseUp = (event: React.MouseEvent, stopId: string) => {
      setIsPressed(false);
      if (stopId && onSelect) {
        if (ROUTE_STOPS_BLACKLIST.includes(stopId)) return;
        return onSelect(stopId);
      }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
      setIsPressed(true);
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      e.stopPropagation();
    };

    const handleTouchEnd = (e: React.TouchEvent, stopId: string) => {
      setIsPressed(false);
      if (stopId && onSelect && !e.defaultPrevented) {
        if (ROUTE_STOPS_BLACKLIST.includes(stopId)) return;
        const touchEnd = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
        const distance = Math.sqrt(
          Math.pow(touchEnd.x - (touchStart?.x || 0), 2) + Math.pow(touchEnd.y - (touchStart?.y || 0), 2)
        );
        if (distance < 10) {
          // Consider it a tap if the touch movement is less than 10px
          return onSelect(stopId);
        }
      }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      e.preventDefault();
    };

    return (
      // Main container
      <Grid
        component="div"
        ref={ref}
        style={{
          display: `flex`,
          flexDirection: `row`,
          width: `100%`,
          zIndex: `1001`,
          position: `relative`,

          ...containerStyles,
          backgroundColor: isPressed ? colors.pressed : undefined,
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={(event) => handleMouseUp(event, stop.id)}
        onMouseLeave={() => setIsPressed(false)} // Reset if mouse leaves the grid
        onTouchStart={handleTouchStart}
        onTouchEnd={(event) => handleTouchEnd(event, stop.id)}
        onTouchMove={handleTouchMove}
      >
        {/* Position and 'tracking position' container */}
        <Grid style={{ display: `flex`, flexDirection: `column`, alignItems: `center` }}>
          <BarDiv style={{ height: stop.isSpecificStop ? undefined : `24px` }} barStyle={topStyle} {...colors}></BarDiv>
          <TagContainer
            tagStyle={tagStyle}
            style={{ position: `relative`, minHeight: `24px`, opacity: done ? `0.7` : 1 }}
          >
            {(showCustomerField || showInProgress || showOptimizationWarning) && (
              <div
                style={{
                  backgroundColor: showInProgress
                    ? RoutalPalette.success.medium
                    : showCustomerField
                    ? RoutalPalette.warning.medium
                    : RoutalPalette.error.medium,
                  borderRadius: `50%`,
                  height: `14px`,
                  width: `14px`,
                  top: `-3px`,
                  right: `-5px`,
                  position: `absolute`,
                  display: `flex`,
                  justifyContent: `center`,
                  alignItems: `center`,
                }}
              >
                {showInProgress ? (
                  <TimeIcon style={{ width: `10px`, height: `10px`, color: RoutalPalette.white }} />
                ) : (
                  <>
                    {showCustomerField ? (
                      <InstructionIcon style={{ width: `10px`, height: `10px`, color: RoutalPalette.white }} />
                    ) : (
                      <WarningIcon
                        color={RoutalPalette.white}
                        style={{ width: `10px`, height: `10px`, color: RoutalPalette.white }}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            <TagText tagStyle={tagStyle} stopType={stopType} {...colors}>
              {stop.isSpecificStop ? (
                <>
                  {stop.id === ROUTE_START_LOCATION ? (
                    <StartIcon
                      style={{ fill: `${RoutalPalette.white}`, height: `14px`, width: `14px`, paddingBottom: `3px` }}
                    />
                  ) : (
                    <EndIcon
                      style={{
                        fill: `${tagStyle === `unhighlighted` ? colors.routeColor : RoutalPalette.white}`,
                        height: `18px`,
                        width: `18px`,
                        paddingLeft: `3px`,
                        paddingTop: `3px`,
                      }}
                    />
                  )}
                </>
              ) : (
                stop.order + 1
              )}
            </TagText>
          </TagContainer>
          <BarDiv barStyle={botStyle} {...colors}></BarDiv>
        </Grid>

        {/* Stop information container */}
        <Grid
          style={{
            display: `flex`,
            flexDirection: `column`,
            width: `100%`,
            marginLeft: `15px`,
            opacity: done ? `0.7` : 1,
          }}
        >
          <VSpacer small />
          <LabelStop
            labelStyles={{
              color: done ? RoutalPalette.secondary00 : RoutalPalette.text,
              fontSize: `16px`,
              fontWeight: `500`,
              height: `fit-content`,
            }}
          >
            {stop.location?.label}
          </LabelStop>
          {stop.label ? (
            <LabelStop labelStyles={{ color: RoutalPalette.secondary00, fontSize: `13px`, height: `fit-content` }}>
              {stop.label}
            </LabelStop>
          ) : null}
          <VSpacer small />

          {showCustomerField && (
            <>
              <Grid style={{ display: `flex` }}>
                <Grid style={{ display: `flex`, flexDirection: `row`, alignItems: `center` }}>
                  <InstructionIcon
                    alt="Customer fields"
                    style={{ height: `15px`, width: `15px`, paddingRight: `8px`, color: RoutalPalette.warning.medium }}
                  />
                </Grid>
                <VSpacer small />
                <Text variant="body">{stop.customer_text_field}</Text>
              </Grid>
              <VSpacer small />
            </>
          )}

          {additionalChips ? (
            <>
              <Grid flexWrap={`wrap`} style={{ display: `flex`, alignItems: `center` }}>
                {additionalChips.map((c: ReactElement, index) => (
                  <Grid style={{ width: `fit-content` }} key={index}>
                    {c}
                  </Grid>
                ))}
              </Grid>

              <VSpacer small />
            </>
          ) : null}
        </Grid>
      </Grid>
    );
  }
);
